/** biome-ignore-all lint/style/useNamingConvention: camel case */
import {useState} from 'react';
import {createPortal} from 'react-dom';
import {shallowEqual, useDispatch, useSelector, useStore} from 'react-redux';
import {SettingsOverlay} from '~/ViewInvitation/common/SettingsOverlay';
import {updateGuestNotificationSettings} from '~/ViewInvitation/reducers/apiActions';
import {EVENT_OPTIONS_UPDATED, EVENT_REMINDERS_UPDATED} from '~/ViewInvitation/reducers/constants';
import {isPreview} from '~/ViewInvitation/utils';
import {Button} from '~library/atoms/Button';
import {toastAlert} from '~library/atoms/Toast';
import {Typography} from '~library/atoms/Typography';
import {AlertLineIcon} from '~library/svg/AlertLineIcon';
import {CheckmarkCircleIcon} from '~library/svg/CheckmarkCircleIcon';
import {ChevronIcon} from '~library/svg/ChevronIcon';
import {NotificationLineIcon} from '~library/svg/NotificationLineIcon';

import './SettingsModule.sass';

export const SettingsModule = ({preview = isPreview()}) => {
  const dispatch = useDispatch();
  const store = useStore();
  const urlParams = new URLSearchParams(window.location.search);
  const settingsParam = urlParams.has('settings') ? parseInt(urlParams.get('settings'), 10) : 0;
  const [overlayOpen, setOverlayOpen] = useState(settingsParam === 1);
  const isHost = useSelector((state) => state.user?.isHost ?? false, shallowEqual);
  const originalSettings = useSelector((state) => state.rsvp.notifications, shallowEqual);
  const reminders = useSelector((state) => state.eventDetails?.reminders);
  const eventId = useSelector((state) => state.eventDetails?.eventId);
  const startDate = useSelector((state) => state.eventDetails?.start);

  const submitSettings = ({activityInvolvingMe, eventActivities, guestRSVPs}, newReminders) =>
    new Promise((resolve, reject) => {
      const notifications = {
        ...originalSettings,
        notify_only_my_activity: activityInvolvingMe || eventActivities,
        email_new_comments: eventActivities,
        email_new_rsvp: guestRSVPs,
      };

      // Update notifications
      dispatch(
        updateGuestNotificationSettings(
          notifications,
          async () => {
            if (isHost) {
              const url = `/ajax/event/${eventId}/update`;
              const data = {
                id: eventId,
                event: {
                  event_reminder_0:
                    newReminders.length > 0 && newReminders[0] ? newReminders[0] : null,
                  event_reminder_1:
                    newReminders.length > 1 && newReminders[1] ? newReminders[1] : null,
                },
              };
              try {
                const response = await evite.fetch(url, {
                  method: 'PATCH',
                  body: JSON.stringify(data),
                  credentials: 'include',
                  cache: 'no-cache',
                  headers: {
                    'X-CSRFToken': evite.cookie('csrftoken'),
                    Accept: 'application/json',
                    'Content-Type': 'application/json; charset=utf-8',
                  },
                });
                if (!response.ok) {
                  const responseJSON = await response.json();
                  if (responseJSON?.message) {
                    toastAlert(responseJSON.message, {
                      icon: <AlertLineIcon />,
                      type: 'error',
                    });
                  }
                } else {
                  const responseJSON = await response.json();
                  store.dispatch({
                    type: EVENT_REMINDERS_UPDATED,
                    payload: newReminders.filter((r) => !!r),
                  });
                  store.dispatch({
                    type: EVENT_OPTIONS_UPDATED,
                    payload: responseJSON.options,
                  });
                  toastAlert('Settings updated.', {
                    icon: <CheckmarkCircleIcon />,
                    type: 'success',
                  });
                }
                setOverlayOpen(false);
                resolve();
              } catch (_e) {
                toastAlert('Unable to update event reminder settings. Please try again.', {
                  icon: <AlertLineIcon />,
                  type: 'error',
                });
                setOverlayOpen(false);
                reject();
              }
            } else {
              toastAlert('Settings updated.', {type: 'success'});
              setOverlayOpen(false);
              resolve();
            }
          },
          (_dispatchFn, _getState, {results}) => {
            setOverlayOpen(false);
            const message = results?.message;
            toastAlert(message || 'Unable to update settings. Please try again.', {
              icon: <AlertLineIcon />,
              type: 'error',
            });
            reject();
          }
        )
      );
    });

  return (
    <>
      <Button
        variant="unstyled-button"
        onClick={preview ? undefined : () => setOverlayOpen(true)}
        className="view-invitation__event-settings-cta"
        data-qa-id="event-settings"
      >
        <NotificationLineIcon ratio={0.75} />
        <Typography
          variant="header6"
          color="charcoal"
          classNames="view-invitation__event-settings-cta__title"
          data-qa-id="settings-module-title"
          semibold
        >
          Notification preferences
        </Typography>
        <ChevronIcon direction="right" />
      </Button>
      {!preview &&
        createPortal(
          <SettingsOverlay
            notificationDefaults={{
              guestRSVPs: originalSettings.email_new_rsvp,
              eventActivities: originalSettings.email_new_comments,
              activityInvolvingMe: originalSettings.notify_only_my_activity,
            }}
            isHost={isHost}
            reminders={reminders}
            open={overlayOpen}
            onClose={() => setOverlayOpen(false)}
            onSave={submitSettings}
            eventDate={new Date(startDate).getTime()}
          />,
          document.body
        )}
    </>
  );
};
